import React from 'react';
import type { BudgetGoal } from '@/lib/api';
import { BUDGET_GOALS_TOTAL_HUNDREDTHS } from '@/lib/constants';
import { formatPercent } from '@/lib/format';
import { translateBudgetType } from '@/lib/translations';
import { toHundredths } from '@/lib/utils/budget';
import { GOAL_SHADES } from './goalShades';

export interface GoalsSplitBarProps {
  /** The draft — what the sliders say right now, saved or not. */
  goals: BudgetGoal[];
  className?: string;
}

// The whole split at once: one income, six parts. Short of 100% the bar leaves its gap empty; past 100% it scales
// to the total so nothing spills out, and the verdict beside the total says by how much. Out of the accessibility
// tree — each row below names its group and value, and the total is a status line.
export const GoalsSplitBar: React.FC<GoalsSplitBarProps> = ({ goals, className }) => {
  const parts = goals.map((goal) => ({ type: goal.type, hundredths: Math.max(toHundredths(goal.percentage), 0) }));
  // The scale only: segment widths of an unsaved draft, the same form-state arithmetic as the running total.
  const scale = Math.max(
    parts.reduce((sum, part) => sum + part.hundredths, 0),
    BUDGET_GOALS_TOTAL_HUNDREDTHS
  );

  return (
    <div className={className} aria-hidden="true">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted">
        {parts
          .filter((part) => part.hundredths > 0)
          .map((part) => (
            <div
              key={part.type}
              title={`${translateBudgetType(part.type)} · ${formatPercent(part.hundredths / 100)}`}
              className={`h-full border-r-2 border-background last:border-r-0 transition-[width] duration-150 ease-out motion-reduce:transition-none ${GOAL_SHADES[part.type]}`}
              style={{ width: `${(part.hundredths / scale) * 100}%` }}
            />
          ))}
      </div>
    </div>
  );
};
